/**
 * AWNINGS (2026-10-08, docs/bim-plan/25-awning-study.md — Matt: "make creating awning
 * structures for our projects simple while achieving top tier results … place an awning
 * and set it up to look great and be actually buildable in 5 minutes or less").
 *
 * A hood, canopy or fabric awning over a door or window, hung on ONE face of its host
 * wall. It cuts nothing (the wall-fixture hosting model: hostId + hostOffsetFt + a face).
 * Placed on an opening it carries `openingId` and, while `fit` is 'opening', sizes and
 * places itself from that opening and its exterior casing every time it resolves — move
 * or widen the window and the hood follows.
 *
 * TYPE = the look, a curated firm standard (the types doctrine): the form, the bracket,
 * the roofing, the eave dressing, the fabric. INSTANCE = where and how big: station,
 * face, the fit, projection / pitch overrides, height.
 *
 * Every member is a real piece at its real size (awning.ts holds the sizing rules — the
 * study's §4 — so the geometry, the palette and the cut list all read one source).
 */
import type { CategorySchema } from '../../registry'
import { TAIL_PROFILES } from '../../roofFraming'
import { AWNING_FORMS, AWNING_BRACKETS } from '../../awning'

const IN = 1 / 12
const profileChoices = TAIL_PROFILES.map((p) => ({ value: p.key, label: p.label }))
const ROOFED = ['shed', 'gable', 'concealed', 'rod', 'portico']
const BRACKETED = ['shed', 'gable']

export const awningSchema: CategorySchema = {
  id: 'awning',
  label: 'Awning',
  pickRank: 1,
  hostable: { hostsInto: ['wall'], cutsHost: false },
  typeParams: {
    groups: [
      {
        label: 'Form',
        params: [
          { key: 'form', label: 'Form', kind: 'choice', default: 'shed', choices: AWNING_FORMS.map((f) => ({ value: f.key, label: f.label })) },
          // the portico's own roof shape (a hood is shed or gable by its form)
          {
            key: 'porticoRoof', label: 'Portico Roof', kind: 'choice', default: 'gable',
            choices: [{ value: 'gable', label: 'Gable' }, { value: 'shed', label: 'Shed' }],
            conditionalOn: { param: 'form', equals: ['portico'] },
          },
          { key: 'projectionFt', label: 'Projection', kind: 'length', default: 32 * IN, min: 12 * IN, max: 10 },
          { key: 'slopeRise', label: 'Pitch (rise/12)', kind: 'number', default: 4, min: 0.25, max: 14 },
        ],
      },
      {
        label: 'Support',
        params: [
          { key: 'bracket', label: 'Bracket', kind: 'choice', default: 'knee', choices: AWNING_BRACKETS.map((b) => ({ value: b.key, label: b.label })), conditionalOn: { param: 'form', equals: BRACKETED } },
          {
            key: 'bracketSize', label: 'Bracket Stock', kind: 'choice', default: '4x4',
            choices: [{ value: '4x4', label: '4×4' }, { value: '4x6', label: '4×6' }, { value: '6x6', label: '6×6' }],
            conditionalOn: [{ param: 'form', equals: BRACKETED }, { param: 'bracket', equals: ['knee', 'curved'] }],
          },
          { key: 'armEnd', label: 'Arm End', kind: 'choice', default: 's14', choices: profileChoices, conditionalOn: { param: 'form', equals: BRACKETED } },
          {
            key: 'postSize', label: 'Post', kind: 'choice', default: '6x6',
            choices: [{ value: '4x4', label: '4×4' }, { value: '6x6', label: '6×6' }, { value: '8x8', label: '8×8' }],
            conditionalOn: { param: 'form', equals: ['portico'] },
          },
          // the rod canopy: rods never flatter than this from horizontal (force = load / sin)
          { key: 'rodAngleDeg', label: 'Rod Angle', kind: 'number', default: 45, min: 30, max: 70, conditionalOn: { param: 'form', equals: ['rod'] } },
        ],
      },
      {
        label: 'Roof',
        params: [
          // '' = the firm's standing seam, in the palette's Metals color (copper on a copper project)
          { key: 'roofing', label: 'Roofing', kind: 'material', default: '', conditionalOn: { param: 'form', equals: ROOFED } },
          { key: 'trellis', label: 'Open Trellis (no roofing)', kind: 'bool', default: false, conditionalOn: { param: 'form', equals: ['shed', 'gable', 'portico'] } },
        ],
      },
      {
        label: 'Eave',
        params: [
          { key: 'tails', label: 'Exposed Rafter Tails', kind: 'bool', default: false, conditionalOn: { param: 'form', equals: ['shed', 'gable', 'portico'] } },
          { key: 'tailStyle', label: 'Tail Style', kind: 'choice', default: 's15', choices: profileChoices, conditionalOn: [{ param: 'form', equals: ['shed', 'gable', 'portico'] }, { param: 'tails', equals: true }] },
          // whether a NEW awning of this look starts with a gutter (each awning then has its own
          // Gutters toggle); the gutter itself is the project's — Project Settings → Trim & Eaves
          { key: 'gutter', label: 'Starts With Gutter', kind: 'bool', default: true, conditionalOn: { param: 'form', equals: ROOFED } },
        ],
      },
      {
        label: 'Fabric',
        params: [
          { key: 'stripeA', label: 'Stripe Color', kind: 'text', default: '#1f2426', conditionalOn: { param: 'form', equals: ['fabric'] } },
          { key: 'stripeB', label: 'Ground Color', kind: 'text', default: '#f2efe6', conditionalOn: { param: 'form', equals: ['fabric'] } },
          { key: 'stripeFt', label: 'Stripe Width (0 = solid)', kind: 'length', default: 4.625 * IN, min: 0, max: 1, conditionalOn: { param: 'form', equals: ['fabric'] } },
          { key: 'valanceFt', label: 'Valance', kind: 'length', default: 9 * IN, min: 0, max: 1.5, conditionalOn: { param: 'form', equals: ['fabric'] } },
          {
            key: 'valance', label: 'Valance Edge', kind: 'choice', default: 'straight',
            choices: [{ value: 'straight', label: 'Straight' }, { value: 'scallop', label: 'Scallop' }],
            conditionalOn: { param: 'form', equals: ['fabric'] },
          },
          {
            key: 'fabricSides', label: 'Sides', kind: 'choice', default: 'open',
            choices: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed (wings)' }],
            conditionalOn: { param: 'form', equals: ['fabric'] },
          },
        ],
      },
      {
        label: 'Finish',
        params: [
          // '' = painted wood in the palette's Trim color (heavy timber: stained dark)
          { key: 'frameMaterial', label: 'Wood', kind: 'material', default: '', conditionalOn: { param: 'form', equals: ['shed', 'gable', 'concealed', 'portico'] } },
          { key: 'stained', label: 'Stained (not painted)', kind: 'bool', default: false, conditionalOn: { param: 'form', equals: ['shed', 'gable', 'concealed', 'portico'] } },
          // '' = the palette's Metals (gutters, rods, steel)
          { key: 'metalMaterial', label: 'Metal', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Placement',
        params: [
          { key: 'hostOffsetFt', label: 'Center Along Wall', kind: 'length', default: 0, min: 0, positional: true },
          {
            key: 'face', label: 'On Face', kind: 'choice', default: 'exterior', positional: true,
            choices: [{ value: 'exterior', label: 'Exterior Face' }, { value: 'interior', label: 'Interior Face' }],
          },
          // the door / window this awning dresses (set by placing on it)
          { key: 'openingId', label: 'Over Opening', kind: 'data', default: '', positional: true },
          {
            key: 'fit', label: 'Size', kind: 'choice', default: 'opening',
            choices: [{ value: 'opening', label: 'Fit the Opening' }, { value: 'manual', label: 'Set Width' }],
          },
          { key: 'widthFt', label: 'Width', kind: 'length', default: 6, min: 1, max: 60, conditionalOn: { param: 'fit', equals: ['manual'] } },
          // past the casing, each side (the study's "opening + casing + 6 in. each side")
          { key: 'marginFt', label: 'Past Casing (each side)', kind: 'length', default: 6 * IN, min: 0, max: 4, conditionalOn: { param: 'fit', equals: ['opening'] } },
        ],
      },
      {
        label: 'Shape',
        params: [
          { key: 'projectionFt', label: 'Projection', kind: 'length', default: null, optional: true, min: 12 * IN, max: 12 },
          { key: 'slopeRise', label: 'Pitch (rise/12)', kind: 'number', default: null, optional: true, min: 0.25, max: 14 },
          // fabric: the drop (valance included); blank = equal to the projection (the industry rule)
          { key: 'dropFt', label: 'Drop', kind: 'length', default: null, optional: true, min: 0.5, max: 6, optionalHint: 'equals projection' },
        ],
      },
      {
        label: 'Height',
        params: [
          {
            key: 'heightMode', label: 'Set By', kind: 'choice', default: 'opening',
            choices: [{ value: 'opening', label: 'Above the Opening' }, { value: 'level', label: 'Height Above Floor' }],
          },
          // fascia bottom above the head casing (the study: 4–8 in.)
          { key: 'clearanceFt', label: 'Above Head Casing', kind: 'length', default: 6 * IN, min: 0, max: 4, conditionalOn: { param: 'heightMode', equals: ['opening'] } },
          { key: 'bottomFt', label: 'Underside Height', kind: 'length', default: 8.5, min: 1, max: 40, conditionalOn: { param: 'heightMode', equals: ['level'] } },
        ],
      },
      {
        // the project's gutter and downspouts (the roofs' own), on or off for this awning
        label: 'Drainage',
        params: [
          { key: 'gutters', label: 'Gutters', kind: 'bool', default: true },
          { key: 'downspouts', label: 'Downspouts', kind: 'bool', default: true, conditionalOn: { param: 'gutters', equals: true } },
        ],
      },
    ],
  },
}
